import React from 'react';
import { Box, Text } from '@chakra-ui/react';

// A small, dependency-free renderer for what the assistant's replies
// actually use — bold text and numbered/bulleted lists — not a spec-
// compliant markdown parser. Chosen over a real markdown package (e.g.
// react-markdown) since the assistant's answers are short, plain-language
// event summaries — never tables/code blocks/nested structures — so a
// full parser would be a lot of dependency weight for no real benefit.
// Kept identical to react_dashboard's copy of the same file (that app's
// AssistantOverlay.js) — this one has no shared-code setup with that
// separate package, so it's duplicated rather than imported cross-app.
//
// Returns React nodes, never raw HTML (no dangerouslySetInnerHTML) — so
// there's no XSS surface to sanitize against even though the source text
// is model-generated, not typed by the viewing user.

const BOLD_RE = /(\*\*[^*]+\*\*)/g;
const BULLET_RE = /^\s*[-*]\s+(.*)/;
const NUMBERED_RE = /^\s*\d+[.)]\s+(.*)/;

const renderInline = (text, keyPrefix) => text
  .split(BOLD_RE)
  .filter(Boolean)
  .map((part, i) => {
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    return bold
      ? <strong key={`${keyPrefix}-${i}`}>{bold[1]}</strong>
      : <React.Fragment key={`${keyPrefix}-${i}`}>{part}</React.Fragment>;
  });

export const renderMarkdownLite = (text) => {
  if (!text) return null;

  const blocks = [];
  let list = null; // { tag: 'ul' | 'ol', items: [] }
  let paragraph = [];

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ tag: 'p', text: paragraph.join('\n') });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list) {
      blocks.push(list);
      list = null;
    }
  };

  for (const line of text.split('\n')) {
    const bullet = line.match(BULLET_RE);
    const numbered = line.match(NUMBERED_RE);
    if (bullet || numbered) {
      flushParagraph();
      const tag = bullet ? 'ul' : 'ol';
      if (!list || list.tag !== tag) {
        flushList();
        list = { tag, items: [] };
      }
      list.items.push((bullet || numbered)[1]);
    } else if (line.trim() === '') {
      flushList();
      flushParagraph();
    } else {
      flushList();
      paragraph.push(line);
    }
  }
  flushList();
  flushParagraph();

  return blocks.map((block, i) => {
    const isLast = i === blocks.length - 1;
    if (block.tag === 'p') {
      return (
        <Text key={i} mb={isLast ? 0 : 2} whiteSpace="pre-wrap">
          {renderInline(block.text, `p${i}`)}
        </Text>
      );
    }
    return (
      <Box as={block.tag} key={i} pl={5} mb={isLast ? 0 : 2} sx={{ listStyleType: block.tag === 'ul' ? 'disc' : 'decimal' }}>
        {block.items.map((item, j) => <li key={j}>{renderInline(item, `${block.tag}${i}-${j}`)}</li>)}
      </Box>
    );
  });
};
